import { useMemo, useState } from "react";

import { formatDecimal, useAlertContext, useConfirmationDialogContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import { CalculationParameterKey, CalculationParameterResponse, settingsApi } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { nameof } from "@/common/helper/commonHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { ParameterEntryFormValues, ParameterFormValues } from "../CalculationParametersSettings.types";
import { createEmptyEntry, toFormValues } from "../CalculationParametersSettings.utils";
import { calculationParameterValidationSchema } from "../calculationParameterValidationSchema";

function removeAt<T extends unknown[]>(items: T, index: number): T {
  return items.filter((_, i) => i !== index) as T;
}

export function useCalculationParametersSettings() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const [selectedKey, setSelectedKey] = useState<CalculationParameterKey | null>(null);

  const { data: parameters = [] } = useQuery({
    queryKey: [QueryKey.CalculationParameters],
    queryFn: () => settingsApi.getAllCalculationParameters(),
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Default to the first parameter until the Supervisor explicitly picks another one.
  const selectedParameter = useMemo(
    () => parameters.find((parameter) => parameter.key === selectedKey) ?? parameters[0] ?? null,
    [parameters, selectedKey]
  );

  const initialValues = useMemo(() => toFormValues(selectedParameter), [selectedParameter]);

  const invalidateParameters = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.CalculationParameters] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.SeizableIncomePeriods] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: ParameterFormValues) => {
      if (selectedParameter === null) {
        throw new Error("No parameter selected");
      }
      return settingsApi.saveCalculationParameterEntries({
        key: selectedParameter.key,
        saveCalculationParameterEntriesRequest: {
          entries: values.entries.map((entry) => ({
            id: entry.id,
            effectiveFrom: entry.effectiveFrom,
            value: entry.value ?? 0,
          })),
        },
      });
    },
    onSuccess: () => {
      invalidateParameters();
      showSuccess(t("settings.parameters.saveSuccess"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ key, entryId }: { key: CalculationParameterKey; entryId: string }) =>
      settingsApi.deleteCalculationParameterEntry({ key, entryId }),
    onSuccess: () => {
      invalidateParameters();
      showSuccess(t("settings.parameters.deleteSuccess"));
    },
  });

  const validationSchema = useMemo(
    () => calculationParameterValidationSchema(t, selectedParameter, parameters),
    [t, selectedParameter, parameters]
  );

  const formik = useFormikWithDirtyForm<ParameterFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => saveMutation.mutateAsync(values),
  });

  const handleSelectParameter = (parameter: CalculationParameterResponse | null) => {
    setSelectedKey(parameter?.key ?? null);
  };

  const handleAddEntry = () => {
    void formik.setFieldValue(nameof<ParameterFormValues>("entries"), [createEmptyEntry(), ...formik.values.entries]);
  };

  const handleRemoveNewEntry = (index: number) => {
    const { entries: entryErrors } = formik.errors;
    const { entries: entryTouched } = formik.touched;

    void formik.setFieldValue(nameof<ParameterFormValues>("entries"), removeAt(formik.values.entries, index));
    if (Array.isArray(entryErrors)) {
      formik.setErrors({ ...formik.errors, entries: removeAt(entryErrors, index) });
    }
    if (entryTouched !== undefined) {
      void formik.setTouched({ ...formik.touched, entries: removeAt(entryTouched, index) });
    }
  };

  const handleDeleteEntry = (entry: ParameterEntryFormValues) => {
    if (selectedParameter === null || entry.id === null || entry.effectiveFrom === null || entry.value === null) {
      return;
    }

    const key = selectedParameter.key;
    const entryId = entry.id;

    showConfirmationDialog({
      dialogTitleText: t("settings.parameters.deleteConfirmTitle"),
      dialogText: t("settings.parameters.deleteConfirmText", {
        value: formatDecimal(entry.value, language),
        date: format(entry.effectiveFrom, "dd.MM.yyyy", { locale: locales[language] }),
      }),
      discardText: t("common.cancel"),
      agreeText: t("common.delete"),
      agreeColor: "error",
      onClose: (confirmed: boolean) => {
        if (confirmed) {
          deleteMutation.mutate({ key, entryId });
        }
      },
    });
  };

  return {
    parameters,
    selectedParameter,
    formik,
    isSaving: saveMutation.isPending || deleteMutation.isPending,
    handleSelectParameter,
    handleAddEntry,
    handleRemoveNewEntry,
    handleDeleteEntry,
  };
}
